import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Hourglass, Crown, ArrowUpRight, Info } from "lucide-react";
import DailyIdeaCard from "../components/DailyIdeaCard";
import StoryIdeasCard from "../components/StoryIdeasCard";
import CaptionHashtagCard from "../components/CaptionHashtagCard";
import CoverGeneratorCard from "../components/CoverGeneratorCard";
import OfferGeneratorCard from "../components/OfferGeneratorCard";
import ProInfoDialog from "../components/ProInfoDialog";
import AdminHealthBadge from "../components/AdminHealthBadge";
import AIResourcesSection from "../components/AIResourcesSection";
import LockedToolOverlay from "../components/LockedToolOverlay";
import CompleteProfileBanner from "../components/CompleteProfileBanner";
import ClientUploadLinkCard from "../components/ClientUploadLinkCard";

export default function DashboardHome() {
  const { user, fetchMe } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAgency = user?.role === "agency";
  const isClient = user?.role === "client";
  const isPendingClient = isClient && user?.status === "pending";
  const isFreeClient =
    isClient && user?.status === "approved" && user?.plan !== "pro";
  // Locked tools detection (Step B): derivati dai flag esposti dal backend
  const setup = user?.profile_setup || {};
  const needsCore = isClient && !isPendingClient && (!setup.has_salone || !setup.has_positioning);
  const coreMissing = [];
  if (!setup.has_salone) coreMissing.push("nome del salone");
  if (!setup.has_positioning) coreMissing.push("posizionamento");
  // bumped whenever positioning is saved → forces caption card to refetch
  const [positioningVersion, setPositioningVersion] = useState(0);
  const [showProDialog, setShowProDialog] = useState(false);

  const displayName =
    isClient ? user?.name_salone : user?.full_name || user?.email;

  return (
    <div data-testid="dashboard-home" className="space-y-8 fade-up">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
            {isAdmin ? "Amministratore" : isAgency ? "Area Agenzia" : "Area Cliente"}
          </div>
          {isAdmin && <AdminHealthBadge />}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Benvenuto, {displayName}.
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-xl">
          Questa è la tua dashboard. I servizi verranno attivati passo passo — per ora da qui potrai
          gestire il tuo account.
        </p>
      </div>

      {isPendingClient && (
        <div
          data-testid="client-pending-banner"
          className="rounded-3xl border border-[var(--mp-ink)] bg-[var(--mp-yellow)] p-6 sm:p-8 shadow-brutal-sm"
        >
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-[var(--mp-ink)] text-white grid place-items-center shrink-0">
              <Hourglass className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.24em]">
                Stato account
              </div>
              <div className="font-display text-2xl tracking-tight mt-1">
                Il tuo account è in attesa di approvazione
              </div>
              <p className="mt-2 text-[var(--mp-ink)]/80 max-w-2xl">
                Un membro dell'agenzia <strong>Marketing Parrucchieri SRL</strong> sta verificando
                la tua richiesta. Appena sarai approvato, vedrai qui tutti i servizi attivi per il
                tuo salone (free e premium).
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => fetchMe()}
                  data-testid="client-pending-refresh"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--mp-ink)] text-white text-sm font-medium hover:bg-black transition-colors"
                >
                  Aggiorna stato
                </button>
                <span className="text-xs text-[var(--mp-ink)]/70">
                  Puoi lasciare aperta questa pagina, aggiorneremo lo stato al click.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {isClient && user?.status === "approved" && (
        <CompleteProfileBanner
          onCompleted={() => setPositioningVersion((v) => v + 1)}
        />
      )}

      {/* Link personale per inviare video (Pro: alimenta la Produzione) */}
      {isClient && user?.status === "approved" && user?.plan === "pro" && (
        <ClientUploadLinkCard />
      )}

      {/* ───────────── SEZIONE 1: Risorse AI gratuite (sempre sbloccate) ───────────── */}
      {isClient && !isPendingClient && <AIResourcesSection />}

      {/* Idea video giornaliera — sempre sbloccata, anche senza profilo completo */}
      {isClient && user?.status === "approved" && (
        <DailyIdeaCard defaultEmail={user?.email || ""} />
      )}

      {/* ───────────── SEZIONE 2: Strumenti AI sbloccati col profilo (1 minuto) ───────────── */}
      {isClient && user?.status === "approved" && (
        <>
          <LockedToolOverlay locked={needsCore} missing={coreMissing}>
            <CaptionHashtagCard refreshKey={positioningVersion} />
          </LockedToolOverlay>
          {isFreeClient && (
            <LockedToolOverlay locked={needsCore} missing={coreMissing}>
              <CoverGeneratorCard />
            </LockedToolOverlay>
          )}
          <LockedToolOverlay locked={needsCore} missing={coreMissing}>
            <OfferGeneratorCard />
          </LockedToolOverlay>
        </>
      )}

      {/* ───────────── SEZIONE 3: Strumenti Pro-only (l'overlay "contatta agenzia" è interno alla card) ───────────── */}
      {isClient && user?.status === "approved" && (
        <StoryIdeasCard isPro={user?.plan === "pro"} />
      )}

      {isFreeClient && (
        <section
          data-testid="upgrade-to-pro-card"
          className="rounded-3xl border border-[var(--mp-ink)] bg-[var(--mp-ink)] text-white shadow-brutal-sm overflow-hidden"
        >
          <div className="px-6 py-6 sm:px-8 sm:py-8 flex items-start gap-5 flex-wrap">
            <div className="h-12 w-12 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center shrink-0 border border-[var(--mp-yellow)]">
              <Crown className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/60">
                Passa a Pro
              </div>
              <div className="font-display text-2xl sm:text-3xl tracking-tight mt-1">
                Facciamo noi il 95% del lavoro.
              </div>
              <p className="mt-2 text-white/80 max-w-xl text-sm leading-relaxed">
                Tu mandi il grezzo. Noi tagliamo, mettiamo sottotitoli e
                musica, e pubblichiamo su Facebook, TikTok, YouTube, Google e
                Instagram ogni giorno — senza il tuo sforzo.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowProDialog(true)}
                data-testid="upgrade-discover-btn"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-white/40 hover:border-white text-white text-sm font-semibold transition-colors"
              >
                <Info className="h-4 w-4" />
                Scopri il Pro
              </button>
              <a
                href="https://www.nicobenz.it/prenota"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="upgrade-consult-btn"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[var(--mp-yellow)] hover:bg-[var(--mp-yellow)] text-[var(--mp-ink)] border border-[var(--mp-ink)] font-bold text-sm shadow-brutal-sm hover-brutal-lift"
              >
                <Sparkles className="h-4 w-4" />
                Consulenza gratuita
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Pro info dialog */}
      <ProInfoDialog
        open={showProDialog}
        onOpenChange={setShowProDialog}
      />

      <div className="rounded-3xl border border-[var(--mp-ink)] bg-white p-8 shadow-brutal-sm">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-[var(--mp-yellow)] grid place-items-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-2xl tracking-tight">Presto in arrivo</div>
            <p className="mt-2 text-[var(--mp-muted)] max-w-2xl">
              {isAdmin
                ? "Dal menu laterale puoi approvare o rifiutare le richieste di accesso dei tuoi collaboratori e dei clienti."
                : isAgency
                ? "Dal menu laterale puoi gestire i clienti dei saloni. Presto troverai qui anche gli strumenti per contenuti e campagne."
                : isPendingClient
                ? "Una volta approvato l'account, qui vedrai i servizi disponibili per il tuo salone (calendario editoriale, approvazioni contenuti, analytics)."
                : "Presto troverai qui il tuo piano social: calendario editoriale, approvazioni, analytics del tuo salone."}
            </p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <InfoCard label="Email" value={user?.email} testId="info-email" />
        <InfoCard
          label="Ruolo"
          value={
            isAdmin ? "Amministratore" : isAgency ? "Agenzia" : "Cliente"
          }
          testId="info-role"
        />
        <InfoCard
          label={isClient ? "Referente" : "Stato"}
          value={
            isClient
              ? `${user?.referente_nome} ${user?.referente_cognome}`
              : user?.status === "approved"
              ? "Attivo"
              : user?.status
          }
          testId="info-extra"
        />
      </div>
    </div>
  );
}

function InfoCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[var(--mp-border)] bg-white p-5"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
        {label}
      </div>
      <div className="mt-1.5 font-display text-lg tracking-tight truncate">
        {value || "—"}
      </div>
    </div>
  );
}
